import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/ui/Header';
import CheckoutProgress from '../../components/ui/CheckoutProgress';
import DeliveryStep from './components/DeliveryStep';
import PaymentStep from './components/PaymentStep';
import ReviewStep from './components/ReviewStep';
import OrderSummary from './components/OrderSummary';
import OrderConfirmation from './components/OrderConfirmation';

const CheckoutProcess = () => {
  const [currentStep, setCurrentStep] = useState(1);
  const [deliveryData, setDeliveryData] = useState({});
  const [paymentData, setPaymentData] = useState({});
  const [orderComplete, setOrderComplete] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    // Check if user has items in cart
    const cart = localStorage.getItem('cart');
    if (!cart || JSON.parse(cart).length === 0) {
      navigate('/shopping-cart');
    }

    // Check authentication
    const authToken = localStorage.getItem('authToken');
    if (!authToken) {
      navigate('/user-authentication-login-register');
    }
  }, [navigate]);

  const handleStepNext = () => {
    if (currentStep < 3) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handleStepBack = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleStepClick = (stepNumber) => {
    if (stepNumber <= currentStep) {
      setCurrentStep(stepNumber);
    }
  };

  const handlePlaceOrder = () => {
    // Clear cart after successful order
    localStorage.removeItem('cart');
    setOrderComplete(true);
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return (
          <DeliveryStep
            onNext={handleStepNext}
            deliveryData={deliveryData}
            setDeliveryData={setDeliveryData}
          />
        );
      case 2:
        return (
          <PaymentStep
            onNext={handleStepNext}
            onBack={handleStepBack}
            paymentData={paymentData}
            setPaymentData={setPaymentData}
          />
        );
      case 3:
        return (
          <ReviewStep
            onBack={handleStepBack}
            onPlaceOrder={handlePlaceOrder}
            deliveryData={deliveryData}
            paymentData={paymentData}
          />
        );
      default:
        return null;
    }
  };

  if (orderComplete) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <main className="pt-20 pb-12">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="py-12">
              <OrderConfirmation 
                orderData={{ deliveryData, paymentData }}
              />
            </div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Header />
      
      <main className="pt-20 pb-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Page Header */}
          <div className="text-center mb-8">
            <h1 className="font-heading font-heading-bold text-3xl text-text-primary mb-2">
              Checkout
            </h1>
            <p className="text-text-secondary">
              Complete your order in just a few simple steps
            </p>
          </div>

          {/* Progress Indicator */}
          <CheckoutProgress 
            currentStep={currentStep}
            onStepClick={handleStepClick}
          />

          {/* Main Content */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mt-8">
            {/* Checkout Form */}
            <div className="lg:col-span-2">
              <div className="bg-surface border border-border rounded-card p-6">
                {renderStepContent()}
              </div>
            </div>

            {/* Order Summary Sidebar */}
            <div className="lg:col-span-1">
              <OrderSummary isVisible={!orderComplete} />
            </div>
          </div>

          {/* Mobile Order Summary */}
          <div className="lg:hidden mt-8">
            <OrderSummary isVisible={!orderComplete} />
          </div>
        </div>
      </main>
    </div>
  );
};

export default CheckoutProcess;